import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ApiDataBase from "server";
import UseContext from "hooks/useContext";
import SectionWrapper from "pages/createMission/components/SectionWrapper";
import MonoSelect from "components/componentsForm/monoSelect";
import {
    templateIntitulePrefix,
    getTemplateIntituleSuffix,
    isTemplateNameTaken,
} from "pages/missionTemplates/utils/templateIntitule";
import {
    findQualification,
    getQualificationTaskProposals,
    replaceProposedTasks,
} from "pages/missionTemplates/utils/templateTasks";
import {
    applyQualificationPrefill,
    getQualificationPrefill,
} from "pages/missionTemplates/utils/templateQualificationPrefill";

const QUALIFICATION_TYPES = {
    sale: { name: "Vente", icon: "🛍" },
    food: { name: "Restauration", icon: "🍳" },
    logistic: { name: "Logistique", icon: "📦" },
    administration: { name: "Administration", icon: "🧾" },
    largeDistribution: { name: "Grande distribution", icon: "🛒" },
    industry: { name: "Industrie", icon: "🏭" },
    other: { name: "Autre", icon: "👷" },
};

/** Key of the qualification type (`sale`, `food`…) for a stored sector label. */
export const getQualificationTypeOfActivity = (activity) =>
    Object.keys(QUALIFICATION_TYPES).find(
        (key) => QUALIFICATION_TYPES[key].name === activity
    );

const ActivityTemplate = () => {
    const { id } = useParams();
    const { templateData, setTemplateData, missionTemplates, language } =
        UseContext();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.activity.${key}`, fallback);
    const [typesQualifications, setTypesQualifications] = useState([]);
    const [isWaitingTypes, setIsWaitingTypes] = useState(true);
    const [listQualifications, setListQualifications] = useState([]);
    const namePrefix = templateIntitulePrefix(templateData.qualification);
    // Edit (and duplicate, done server side) never applies the
    // qualification's risk, tools and licences on its own.
    const isCreation = !id;
    const withQualificationPrefill = (data, prefill) =>
        isCreation ? applyQualificationPrefill(data, prefill) : data;

    /** false = on peut encore recalculer le préfixe quand la qualification change (création). */
    const [hasTouchedName, setHasTouchedName] = useState(() => {
        const auto = templateIntitulePrefix(templateData.qualification);
        const stored = templateData.name || "";
        if (!stored.trim()) return false;
        if (!auto) return true;
        return stored !== auto;
    });

    // The wizard only holds the name the user is typing: the conflict is read
    // from the templates already listed for the company.
    const isNameTaken = isTemplateNameTaken(
        templateData.name,
        missionTemplates,
        id
    );

    const selectedType = getQualificationTypeOfActivity(templateData.activity);

    const filteredQualifications = selectedType
        ? listQualifications.filter((q) => q.type === selectedType)
        : [];

    useEffect(() => {
        ApiDataBase.getListQualifications(
            {},
            (res) => setListQualifications(res),
            (err) => console.warn(err)
        );
    }, []);

    useEffect(() => {
        setIsWaitingTypes(true);
        ApiDataBase.getTypesQualifications(
            {},
            (res) => {
                setTypesQualifications(res);
                setIsWaitingTypes(false);
            },
            (err) => {
                console.warn(err);
                setIsWaitingTypes(false);
            }
        );
    }, []);

    const handleTypeClick = (type) => {
        const clearing = selectedType === type;
        setTemplateData((prev) => {
            const next = {
                ...prev,
                activity: clearing ? "" : QUALIFICATION_TYPES[type].name,
                qualification: clearing ? "" : prev.qualification,
                ...(clearing
                    ? { tasks: replaceProposedTasks(prev.tasks, []) }
                    : {}),
                ...(clearing && !hasTouchedName ? { name: "" } : {}),
            };
            return clearing ? withQualificationPrefill(next, null) : next;
        });
    };

    const handleQualificationChoice = (selected) => {
        const value = selected ? selected.value : "";
        setTemplateData((prev) => {
            if (!value) {
                return withQualificationPrefill(
                    {
                        ...prev,
                        qualification: "",
                        name: "",
                        tasks: replaceProposedTasks(prev.tasks, []),
                    },
                    null
                );
            }
            const suffix = getTemplateIntituleSuffix(prev.name, prev.qualification);
            const next = {
                ...prev,
                qualification: value,
                name: templateIntitulePrefix(value) + suffix,
            };
            // Picking the same qualification again keeps the selection.
            if (value === prev.qualification) return next;
            const qualification = findQualification(
                listQualifications,
                value,
                selectedType
            );
            return withQualificationPrefill(
                {
                    ...next,
                    tasks: replaceProposedTasks(
                        prev.tasks,
                        getQualificationTaskProposals(qualification)
                    ),
                },
                getQualificationPrefill(qualification)
            );
        });
    };

    const handleSuffixChange = (e) => {
        setHasTouchedName(true);
        const suffix = e.target.value;
        setTemplateData((prev) => ({
            ...prev,
            name: templateIntitulePrefix(prev.qualification) + suffix,
        }));
    };

    return (
        <div className={"flex flex-col gap-6"}>
            <SectionWrapper
                title={t("sectorTitle", "Secteur d'activité")}
                description={t(
                    "sectorDescription",
                    "Dans quel secteur se situe le poste ?"
                )}
            >
                <div className={"grid grid-cols-3 gap-4"}>
                    {isWaitingTypes
                        ? [1, 2, 3, 4, 5].map((i) => (
                              <div
                                  key={i}
                                  className={
                                      "h-24 flex flex-col justify-center items-center gap-2 border rounded-lg"
                                  }
                              >
                                  <div className={"h-8 bg-gray-200 rounded w-8"} />
                                  <div className={"h-3 bg-gray-200 rounded w-20"} />
                              </div>
                          ))
                        : typesQualifications.map(
                              (type) =>
                                  QUALIFICATION_TYPES[type] && (
                                      <div
                                          key={type}
                                          className={`h-24 flex flex-col justify-center items-center gap-2 border rounded-lg cursor-pointer ${
                                              selectedType === type
                                                  ? "border-yellow-500 bg-yellow-50"
                                                  : "border-gray-200"
                                          }`}
                                          onClick={() => handleTypeClick(type)}
                                      >
                                          <div className={"text-2xl"}>
                                              {QUALIFICATION_TYPES[type].icon}
                                          </div>
                                          <div className={"ft-sm"}>
                                              {QUALIFICATION_TYPES[type].name}
                                          </div>
                                      </div>
                                  )
                          )}
                </div>
            </SectionWrapper>
            <SectionWrapper
                title={t("qualificationTitle", "Qualification")}
                description={t(
                    "qualificationDescription",
                    "Quel est le métier exercé par l'intérimaire ?"
                )}
            >
                <MonoSelect
                    data={filteredQualifications}
                    selectArray={handleQualificationChoice}
                    defaultValue={templateData.qualification}
                    placeholder={t(
                        "qualificationPlaceholder",
                        "Choisir une qualification"
                    )}
                    noOptionsMessage={t(
                        "qualificationNoOptions",
                        "Sélectionnez le secteur d'activité"
                    )}
                    ariaLabel={t("qualificationTitle", "Qualification")}
                />
            </SectionWrapper>
            <SectionWrapper
                title={t("nameTitle", "Intitulé de la fiche de poste")}
                description={t(
                    "nameDescription",
                    "Il commence par la qualification. Complétez après le tiret (ex : Nuit, Rayon frais)."
                )}
            >
                <div>
                    <div
                        className={`flex w-full min-h-10 rounded-lg border overflow-hidden ${
                            !templateData.qualification?.trim()
                                ? "border-gray-200 bg-gray-50 opacity-70"
                                : isNameTaken
                                ? "border-red-500"
                                : "border-gray-300"
                        }`}
                    >
                        {namePrefix && (
                            <span
                                className="shrink-0 max-w-[55%] px-3 py-2 text-sm leading-5 text-gray-800 bg-gray-100 border-r border-gray-200 truncate"
                                title={namePrefix}
                            >
                                {namePrefix}
                            </span>
                        )}
                        <input
                            type="text"
                            className="flex-1 min-w-0 h-10 pl-3 pr-3 ft-b placeholder-gray-600 border-0 focus:outline-none focus:ring-0 disabled:bg-gray-50"
                            value={getTemplateIntituleSuffix(
                                templateData.name,
                                templateData.qualification
                            )}
                            onChange={handleSuffixChange}
                            placeholder={
                                namePrefix
                                    ? t("nameSuffixPlaceholder", "Ex : Nuit")
                                    : t(
                                          "namePlaceholder",
                                          "Ex : Préparatrice de commande - Nuit"
                                      )
                            }
                            aria-label={t(
                                "nameTitle",
                                "Intitulé de la fiche de poste"
                            )}
                            disabled={!templateData.qualification?.trim()}
                        />
                    </div>
                    {isNameTaken && (
                        <p className="mt-1 ft-sm text-red-500">
                            {t(
                                "nameTaken",
                                "Une fiche de poste porte déjà ce nom."
                            )}
                        </p>
                    )}
                </div>
            </SectionWrapper>
        </div>
    );
};

export default ActivityTemplate;
